import * as RD from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';
import { Button, IconButton } from './primitives';
import { Stepper } from './status';
import { Plain } from '@/app/technical';
import { InlineDialog, useInlineFormScope } from './InlineForms';

/**
 * Radix owns focus trapping, return-on-close and the escape key — the three
 * things that are expensive to hand-roll and easy to get subtly wrong.
 * Everything visible is ours.
 */
export interface DialogProps {
  onCloseAutoFocus?: (event: Event) => void;
  size?: 'normal' | 'record';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kicker: string;
  title: string;
  danger?: boolean | undefined;
  children: ReactNode;
  primary?: { label: string; onClick: () => void; disabled?: boolean } | undefined;
  secondary: { label: string; onClick: () => void };
  note?: string | undefined;
  /** A flow of more than one step shows where it is (the gallery's stepper). */
  step?: { at: number; of: number } | undefined;
}

export function Dialog(props: DialogProps) {
  const inline = useInlineFormScope();
  if (inline !== undefined) return <InlineDialog {...props} active={inline} />;
  const { open, onOpenChange, kicker, title, danger, children, primary, secondary, note, step, size = 'normal', onCloseAutoFocus } = props;
  return (
    <RD.Root open={open} onOpenChange={onOpenChange}>
      <RD.Portal>
        <RD.Overlay className="fixed inset-0 z-40 bg-[var(--scrim)]" />
        <RD.Content
          onCloseAutoFocus={onCloseAutoFocus}
          className={`fixed left-1/2 top-1/2 z-50 flex max-h-[88dvh] w-[calc(100%-32px)] ${size === 'record' ? 'max-w-[1120px]' : 'max-w-[560px]'} -translate-x-1/2 -translate-y-1/2 flex-col overflow-y-auto border bg-page phone:inset-0 phone:h-[100dvh] phone:max-h-none phone:w-full phone:max-w-none phone:translate-x-0 phone:translate-y-0 phone:border-0 ${
            danger ? 'border-danger' : 'border-strong'
          }`}
        >
          <header className="flex flex-col gap-2 border-b border-line px-6 pb-4 pt-6">
            {step && <Stepper at={step.at} of={step.of} />}
            <div className="flex items-center gap-3">
              {step && step.of > 1 && (
                <span className="text-micro uppercase tracking-label text-meta">
                  Step {step.at + 1} of {step.of}
                </span>
              )}
              <RD.Close asChild>
                <IconButton label="Close" glyph="close" className="ml-auto" />
              </RD.Close>
            </div>
            <RD.Description
              className={`text-micro font-medium uppercase tracking-label ${danger ? 'text-danger' : 'text-meta'}`}
            >
              <Plain>{kicker}</Plain>
            </RD.Description>
            <RD.Title className="m-0 text-panel font-semibold">{title}</RD.Title>
          </header>
          <div className="flex flex-col gap-5 px-6 py-6">{children}</div>
          <footer className="sticky bottom-0 flex flex-wrap items-center gap-3 border-t border-line bg-raised px-6 py-4 phone:pb-[max(1rem,env(safe-area-inset-bottom))]">
            {primary && (
              <Button
                variant={danger ? 'danger-solid' : 'primary'}
                onClick={primary.onClick}
                disabled={primary.disabled}
              >
                {primary.label}
              </Button>
            )}
            <Button variant="quiet" onClick={secondary.onClick}>
              {secondary.label}
            </Button>
            {note && <span className="ml-auto text-label text-meta"><Plain>{note}</Plain></span>}
          </footer>
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}
